import useGoBack from 'utilities/navigation/back'
import { Field, Form, Formik } from 'formik'
import Header from 'components/headers/addLoan'
import AddNewIcon from 'assets/images/loans/managementAddNew.svg'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import Num2persian from 'utilities/helper/num2Persian'
import { useState } from 'react'
import { toast } from 'react-toastify'
import useGetCards from 'pages/loans/withDraw/hooks/useGetCards'
import SelectModal from 'components/modals/selectModal'
import WithDrawWalletModal from 'components/modals/withDrawWallets'
import FeeModal from 'components/modals/feeModal'
import CustomDatePicker from 'components/datePicker'
import moment from 'jalali-moment'
import { getOneMonthLater } from 'utilities/helper/getOneMonthLater'
import { toJalaliInputDate } from 'utilities/helper/jalaliInputDate'
import { loanInterestFields } from 'utilities/helper/loanInterest'
import useSetNewLoans from 'pages/loans/manager/addNew/hooks/useSetNewLoans'
import { Schema } from 'pages/loans/manager/addNew/utilities'
import { NumericFormat } from 'react-number-format'
import {
    accountNumberOf,
    formatAccountNumber,
} from 'components/bankAccountCard'
interface ISelectUser {
    name: string
    id: undefined | number
    memberId: undefined | number
}

interface IAcceptFormValues {
    amount: string
    tag: string
    number: string
    fee_type: string
    date: string
}

/**
 * What the screen had collected when it last sent the manager on. Typed rather
 * than read off `location.state` as `any`, so Formik keeps inferring a
 * concrete shape for `values` and `errors`.
 */
interface IAcceptFormDraft {
    values?: Partial<IAcceptFormValues>
    selectedUser?: ISelectUser
    selectedFee?: number
    selectedFeeType?: string
    bankAccountId?: number
}
export default function AcceptAddNewManagementLoan({}) {
    const { state, pathname } = useLocation()
    /**
     * What this screen had collected when it last sent the manager on to the
     * instalments step. It is written into THIS history entry (see the
     * `navigate(..., { replace: true })` in `onSubmit`), so every way back —
     * the «بازگشت» button, the header arrow, the phone's own back gesture —
     * lands on a form that still has the answers in it. Reported from real
     * use: «وقتی … برای اصلاح برگشت رو می‌زنیم، کل فرم پاک می‌شه».
     */
    const draft: IAcceptFormDraft | undefined = state?.draft
    const [isOpenReceiver, setIsOpenReceiver] = useState<boolean>(false)
    const [selectedUser, setSelectedUser] = useState<ISelectUser | undefined>(
        draft?.selectedUser ??
            (state && state?.register
                ? {
                      name: state?.register?.name,
                      id: state?.register?.id,
                      memberId: state?.register?.member_id,
                  }
                : undefined)
    )
    const [isOpenWalletModal, setIsOpenWalletModal] = useState<boolean>(false)
    const [selectedBankNumber, setSelectedBankNumber] = useState<number>(0)
    const [memberId, setMemberId] = useState<undefined | number>()
    const [isOpenFeeModal, setIsOpenFeeModal] = useState<boolean>(false)
    const [selectedFee, setSelectedFee] = useState<number | undefined>(
        draft?.selectedFee ?? 0
    )
    const [selectedFeeType, setSelectedFeeType] = useState<string>(
        draft?.selectedFeeType ?? 'percentage'
    )
    const navigate = useNavigate()
    const goBack = useGoBack()
    const params = useParams()

    const {
        data,
        isLoading,
        activeAccount,
        activeAccounts,
        otherAccounts,
        handleSelectCard,
        reallySelectedCard,
    } = useGetCards(
        selectedUser?.id,
        selectedUser?.memberId,
        setSelectedBankNumber,
        draft?.bankAccountId
    )

    const { mutate } = useSetNewLoans()
    return (
        <>
            <Formik
                initialValues={{
                    amount:
                        draft?.values?.amount ??
                        (state && state?.amount ? String(state?.amount) : ''),
                    tag: draft?.values?.tag ?? '',
                    number:
                        draft?.values?.number ??
                        (state && state?.num_repayment
                            ? String(state?.num_repayment)
                            : ''),
                    fee_type: draft?.values?.fee_type ?? '',
                    // Prefilled with a month from today rather than left
                    // empty: an empty picker rendered TODAY, so the screen
                    // showed one first-instalment date and the API was sent
                    // another (this field falls back to `getOneMonthLater()`
                    // when blank). Android has always opened on next month.
                    date: draft?.values?.date ?? getOneMonthLater(),
                }}
                validationSchema={Schema}
                enableReinitialize
                onSubmit={(values) => {
                    if (Number(values.amount) >= 5000) {
                        if (selectedUser) {
                            if (
                                selectedFee !== undefined &&
                                selectedFee !== null
                            ) {
                                if (selectedFee === 0) {
                                    values.fee_type = 'lip_ma'
                                }
                                if (values.fee_type.length > 0) {
                                    // The borrower's account is REQUIRED, by
                                    // decision: a loan is granted against a
                                    // known account, and a request filed
                                    // without one is not something to keep
                                    // accommodating. What was actually broken
                                    // is that this picker used to be handed
                                    // the WRONG member — see the navigation in
                                    // `item/detail`.
                                    if (reallySelectedCard) {
                                        const data: IManagementBodyFirst = {
                                            moneyPool: params?.id,
                                            poolShip: selectedUser.id,
                                            amount: values.amount,
                                            tag: values.tag,
                                            number_of_repayments: values.number,
                                            bank_account:
                                                reallySelectedCard?.id,
                                            bankName: reallySelectedCard
                                                ? reallySelectedCard.full_name +
                                                  '-' +
                                                  reallySelectedCard.bank_name
                                                : undefined,
                                            // carried for the confirmation
                                            // screen only — the API call
                                            // picks its fields by name
                                            bankAccount: reallySelectedCard,
                                            ctx_id: params && Number(params.id),
                                            receiver: selectedUser?.memberId,
                                            name:
                                                selectedUser &&
                                                selectedUser?.name,
                                            interest_pay_mode: values.fee_type,
                                            // `interest_rate` is a fraction:
                                            // this screen used to send the
                                            // picker's number as-is, so «۱
                                            // درصد» became a 100% loan
                                            ...loanInterestFields(
                                                selectedFeeType,
                                                selectedFee
                                            ),
                                            pay_date:
                                                moment().format('YYYY-MM-DD'),
                                            fundName: state?.name,
                                            due_date:
                                                values.date.length > 0
                                                    ? values.date
                                                    : getOneMonthLater(),
                                        }
                                        // park the answers on this history
                                        // entry before leaving, so coming
                                        // back finds them
                                        navigate(pathname, {
                                            replace: true,
                                            state: {
                                                ...(state ?? {}),
                                                draft: {
                                                    values,
                                                    selectedUser,
                                                    selectedFee,
                                                    selectedFeeType,
                                                    bankAccountId:
                                                        reallySelectedCard?.id,
                                                },
                                            },
                                        })
                                        mutate(data)
                                    } else {
                                        toast.error('شماره حساب را انتخاب کنید')
                                    }
                                } else {
                                    toast.error(
                                        'نحوه دریافت کارمزد را انتخاب کنید'
                                    )
                                }
                            } else {
                                toast.error('میزان کارمزد را وارد کنید')
                            }
                        } else {
                            toast.error('دریافت کننده وام را انتخاب کنید')
                        }
                    } else {
                        toast.error('حداقل مبلغ وام پنج هزار تومان می باشد')
                    }
                }}
            >
                {({ values, touched, errors, setFieldValue }) => (
                    <Form>
                        <div className=" mx-3 flex flex-col h-screen text-sm ">
                            <div className=" grow-0">
                                <Header title="اعطای وام" />
                            </div>
                            <div className=" rounded-lg grow">
                                <div className="  bg-white  mt-14 rounded-lg relative py-4 shadow-[0px_2px_6px_-1px_rgba(0,0,0,0.1),0px_1px_0px_0px_rgba(25,28,33,0.02),0px_0px_0px_1px_rgba(25,28,33,0.08)]">
                                    <div className=" text-center mt-4">
                                        <img
                                            src={AddNewIcon}
                                            alt="add-loan"
                                            className=" absolute right-[35%] md:right-1/2 md:translate-x-1/2 -top-14"
                                        />
                                        <p className=" mb-2">موجودی صندوق:</p>
                                        <p className=" text-black">
                                            {state &&
                                                Number(
                                                    state.exist
                                                ).toLocaleString()}{' '}
                                            تومان
                                        </p>
                                    </div>
                                    <div className="  w-full text-center">
                                        <NumericFormat
                                            customInput={Field}
                                            thousandSeparator
                                            allowNegative={false}
                                            name="amount"
                                            placeholder="مبلغ وام (تومان)"
                                            className="w-full outline-0 text-center border-b border-dashed border-gray-1 py-3  "
                                            value={values.amount}
                                            isGray
                                            secondaryTitle="| تومان "
                                            onValueChange={(values) => {
                                                const { value } = values
                                                setFieldValue('amount', value)
                                            }}
                                        />
                                        {values.amount !== '' && (
                                            <span className=" text-xs text-blue-1 text-center w-full">
                                                {Num2persian(
                                                    values.amount.toString()
                                                )}{' '}
                                                تومان
                                            </span>
                                        )}
                                        {errors.amount && touched.amount && (
                                            <p className=" text-sm text-error-3 text-center">
                                                {errors.amount}
                                            </p>
                                        )}

                                        <div className="w-full outline-0 text-center border-b border-dashed border-gray-1 py-3 text-placeholder bg-white">
                                            {selectedUser?.name
                                                ? selectedUser?.name
                                                : 'دریافت کننده وام'}
                                        </div>
                                        <div className=" w-full text-center">
                                            <Field
                                                type="text"
                                                inputMode="numeric"
                                                className="w-full outline-0 text-center border-b border-dashed border-gray-1 py-3  "
                                                name="number"
                                                placeholder="تعداد اقساط"
                                                min={0}
                                            />
                                            {errors.number &&
                                                touched.number && (
                                                    <p className=" text-sm text-error-3 text-center">
                                                        {errors.number}
                                                    </p>
                                                )}
                                        </div>
                                        <div
                                            onClick={() =>
                                                setIsOpenFeeModal(true)
                                            }
                                            className="w-full outline-0 text-center border-b border-dashed border-gray-1 py-3 text-placeholder bg-white"
                                        >
                                            {selectedFee !== undefined &&
                                            selectedFee !== null &&
                                            selectedFee !== 0
                                                ? `${selectedFee} ${
                                                      selectedFeeType ===
                                                      'percentage'
                                                          ? 'درصد'
                                                          : 'تومان'
                                                  }`
                                                : 'میزان کارمزد'}
                                        </div>
                                        {selectedFee !== 0 && (
                                            <Field
                                                name="fee_type"
                                                as="select"
                                                className="w-full outline-0 text-center border-b border-dashed border-gray-1 py-3"
                                            >
                                                <option className="text-gray-1">
                                                    نحوه دریافت کارمزد
                                                </option>
                                                <option value="lip_ma">
                                                    افزودن به هر قسط
                                                </option>
                                                <option value="lip_pr">
                                                    کسر از مبلغ وام
                                                </option>
                                            </Field>
                                        )}

                                        <div
                                            dir="rtl"
                                            className="border-b border-dashed border-gray-1"
                                        >
                                            <>
                                                <CustomDatePicker
                                                    value={toJalaliInputDate(
                                                        values.date
                                                    )}
                                                    setValue={(e: string) => {
                                                        setFieldValue('date', e)
                                                    }}
                                                    name={'date'}
                                                    title="تاریخ شروع اقساط"
                                                    placeHolder="تاریخ شروع اقساط"
                                                />
                                            </>
                                        </div>
                                        {/* The account the member chose when
                                            they filed the request. Its number
                                            is shown here, not only inside the
                                            picker: a manager who is going to
                                            hand the money over themselves
                                            needs to read it, and «فقط ثبت
                                            شود» is two screens further on. */}
                                        <div
                                            onClick={() => {
                                                selectedUser
                                                    ? setIsOpenWalletModal(true)
                                                    : toast.error(
                                                          'دریافت کننده را انتخاب کنید'
                                                      )
                                            }}
                                            className="w-full outline-0 text-center border-b border-dashed border-gray-1 py-3 text-placeholder bg-white"
                                        >
                                            {reallySelectedCard ? (
                                                <>
                                                    <span>
                                                        {reallySelectedCard.full_name +
                                                            '-' +
                                                            reallySelectedCard.bank_name}
                                                    </span>
                                                    {accountNumberOf(
                                                        reallySelectedCard
                                                    ) && (
                                                        <span
                                                            dir="ltr"
                                                            className=" mt-1 block text-black tracking-wide"
                                                        >
                                                            {formatAccountNumber(
                                                                accountNumberOf(
                                                                    reallySelectedCard
                                                                )!.value
                                                            )}
                                                        </span>
                                                    )}
                                                </>
                                            ) : (
                                                'شماره حساب'
                                            )}
                                        </div>
                                        <Field
                                            as="textarea"
                                            name="tag"
                                            className="w-full outline-0 text-center py-4 px-4 text-sm"
                                            placeholder="توضیحات(اختیاری)"
                                        />
                                        {errors.tag && touched.tag && (
                                            <p className=" text-sm text-error-3 text-center">
                                                {errors.tag}
                                            </p>
                                        )}
                                    </div>
                                </div>
                            </div>
                            <button
                                className=" grow-0 bg-green-1 text-white py-3 rounded-lg"
                                type="submit"
                            >
                                تایید
                            </button>
                            <button
                                type="button"
                                className=" text-center my-3 text-gray-4"
                                onClick={goBack}
                            >
                                بعدا
                            </button>
                        </div>
                    </Form>
                )}
            </Formik>
            <SelectModal
                show={isOpenReceiver}
                onHide={() => setIsOpenReceiver(false)}
                setSelectedUser={setSelectedUser}
                setMemberId={setMemberId}
            />
            <WithDrawWalletModal
                show={isOpenWalletModal}
                onHide={() => setIsOpenWalletModal(false)}
                data={!isLoading && data?.receivers?.[0]?.bankaccounts}
                activeAccounts={activeAccounts}
                otherAccounts={otherAccounts}
                selectedUser={selectedUser?.name}
                setSelectedBankNumber={setSelectedBankNumber}
                handleSelectCard={handleSelectCard}
                receiverMemberId={selectedUser?.memberId}
            />
            <FeeModal
                show={isOpenFeeModal}
                onHide={() => setIsOpenFeeModal(false)}
                setSelectedFee={setSelectedFee}
                setSelectedFeeType={setSelectedFeeType}
            />
        </>
    )
}
